import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { OAuth2AuthCodePKCE } from '@bity/oauth2-auth-code-pkce';
import { HttpClient, LichessClient } from './LichessClient';

export const scopes = ['board:play', 'challenge:read', 'challenge:write', 'preference:read'];
export const clientId = 'lichess-api-chess-sense';

export interface Me {
    id: string;
    username: string;
    lichessClient: LichessClient;
}

interface AuthContextValue {
    me?: Me;
    login: () => Promise<void>;
    loginWithToken: (token: string) => Promise<boolean>;
    logout: () => Promise<void>;
    isLoading: boolean;
    error: string | null;
    clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const createPatHttpClient = (token: string, host: string): HttpClient => {
    return async (url: string, options?: RequestInit) => {
        const fullUrl = url.startsWith('http') ? url : `${host}${url}`;
        const headers = new Headers(options?.headers || {});
        headers.set('Authorization', `Bearer ${token}`);
        return fetch(fullUrl, {
            ...options,
            headers,
        });
    };
};

export const AuthProvider: React.FC<{ lichessHost: string; children: React.ReactNode }> = ({
    lichessHost,
    children,
}) => {
    const [me, setMe] = useState<Me | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const redirectUrl = useMemo(
        () => `${window.location.origin}/connect`,
        []
    );

    const oauth = useMemo(
        () =>
            new OAuth2AuthCodePKCE({
                authorizationUrl: `${lichessHost}/oauth`,
                tokenUrl: `${lichessHost}/api/token`,
                clientId,
                scopes,
                redirectUrl,
                onAccessTokenExpiry: (refreshAccessToken) => refreshAccessToken(),
                onInvalidGrant: console.warn,
            }),
        [lichessHost, redirectUrl]
    );

    const clearOAuthParamsFromUrl = () => {
        if (window.history.replaceState) {
            const url = new URL(window.location.href);
            if (
                url.searchParams.has('code') ||
                url.searchParams.has('state') ||
                url.searchParams.has('error') ||
                url.searchParams.has('error_description')
            ) {
                url.searchParams.delete('code');
                url.searchParams.delete('state');
                url.searchParams.delete('error');
                url.searchParams.delete('error_description');
                const cleanUrl = url.pathname + (url.search ? url.search : '') + url.hash;
                window.history.replaceState({}, document.title, cleanUrl);
            }
        }
    };

    const authenticate = useCallback(
        async (customHttpClient?: HttpClient) => {
            const httpClient = customHttpClient || oauth.decorateFetchHTTPClient(window.fetch);
            const res = await httpClient(`${lichessHost}/api/account`);
            if (res.status === 401) {
                throw new Error('Unauthorized: Invalid or expired token');
            }
            if (res.status === 429) {
                throw new Error('Lichess rate limit exceeded. Please wait a moment.');
            }
            if (!res.ok) {
                throw new Error(`Failed to fetch account info: ${res.status} ${res.statusText}`);
            }
            const data = await res.json();
            if ((data as any).error) {
                throw new Error((data as any).error);
            }
            const meData: Me = {
                ...data,
                lichessClient: new LichessClient(httpClient, lichessHost),
            };
            setMe(meData);
            setError(null);
            clearOAuthParamsFromUrl();
            return meData;
        },
        [lichessHost, oauth]
    );

    const init = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            // 1. Check if returning from Lichess OAuth redirect with authorization code
            const hasAuthCode = await oauth.isReturningFromAuthServer().catch((err) => {
                console.warn('OAuth redirect check returned error:', err);
                return false;
            });

            if (hasAuthCode) {
                // Exchange authorization code for access token via PKCE
                await oauth.getAccessToken();
                await authenticate();
                clearOAuthParamsFromUrl();
                setIsLoading(false);
                return;
            }

            // 2. Check if existing OAuth session is active
            if (oauth.isAuthorized()) {
                try {
                    await authenticate();
                    setIsLoading(false);
                    return;
                } catch (authErr) {
                    console.warn('Existing OAuth token is invalid/expired. Resetting.', authErr);
                    oauth.reset();
                    localStorage.removeItem('oauth2authcodepkce-state');
                }
            }

            // 3. Check if Personal Access Token was previously saved
            const savedPat = localStorage.getItem('lichess_pat');
            if (savedPat) {
                try {
                    const patClient = createPatHttpClient(savedPat, lichessHost);
                    await authenticate(patClient);
                    setIsLoading(false);
                    return;
                } catch (patErr) {
                    console.warn('Saved Personal Access Token is invalid/expired. Removing.', patErr);
                    localStorage.removeItem('lichess_pat');
                }
            }
        } catch (err: any) {
            console.error('Lichess auth init error:', err);
            setError(err?.message || 'Failed to authenticate with Lichess');
            clearOAuthParamsFromUrl();
        } finally {
            setIsLoading(false);
        }
    }, [oauth, lichessHost, authenticate]);

    const login = useCallback(async () => {
        setError(null);
        try {
            await oauth.fetchAuthorizationCode();
        } catch (err: any) {
            console.error('Failed to initiate Lichess login:', err);
            setError(err?.message || 'Failed to open Lichess login');
        }
    }, [oauth]);

    const loginWithToken = useCallback(
        async (token: string): Promise<boolean> => {
            setIsLoading(true);
            setError(null);
            const trimmedToken = token.trim();
            if (!trimmedToken) {
                setError('Please enter a valid Lichess API token');
                setIsLoading(false);
                return false;
            }

            try {
                const patClient = createPatHttpClient(trimmedToken, lichessHost);
                await authenticate(patClient);
                localStorage.setItem('lichess_pat', trimmedToken);
                return true;
            } catch (err: any) {
                console.error('PAT login error:', err);
                setError(err?.message || 'Invalid Personal Access Token');
                return false;
            } finally {
                setIsLoading(false);
            }
        },
        [lichessHost, authenticate]
    );

    const logout = useCallback(async () => {
        setIsLoading(true);
        try {
            if (me) {
                await me.lichessClient.request('/api/token', { method: 'DELETE' }).catch(() => {});
            }
        } catch (err) {
            console.warn('Error revoking token:', err);
        }
        oauth.reset();
        localStorage.removeItem('oauth2authcodepkce-state');
        localStorage.removeItem('lichess_pat');
        setMe(undefined);
        setError(null);
        setIsLoading(false);
    }, [me, oauth]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider
            value={{
                me,
                login,
                loginWithToken,
                logout,
                isLoading,
                error,
                clearError,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextValue => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
};
